import { Fetcher } from '@this/src/util';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import _ from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment';
import Notification from '@this/src/notification';
import type { HotelOrderItemAgodaTotalResponseArgs } from '@this/src/domain/arrangement/hotel_order_item_agoda_total';
import HotelOrderItemAgodaTotal, {
  convertHotelOrderItemAgodaTotalResponseToArgs
} from '@this/src/domain/arrangement/hotel_order_item_agoda_total';
import type { ArrangerArgs } from '@this/src/domain/arranger/arranger';
import Arranger from '@this/src/domain/arranger/arranger';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Button } from '@this/components/shared/ui/inputs/button';
import DateRangePicker from '../../../date_range_picker';
import TotalSearchArea from './total_search_area';
import TotalResultTable from './total_result_table';

interface FetchAction {
  id: number;
  status: string;
  from_date: string;
  to_date: string;
  fetched_count: number;
  finished_at: string | null;
  error_message: string | null;
}

interface AgodaTotalResponse {
  records: HotelOrderItemAgodaTotalResponseArgs[];
  arrangers: ArrangerArgs[];
  supplied_item_id: number | null;
  supplied_item_name: string | null;
  current_arranger: ArrangerArgs;
  total_count: number;
  total_page: number;
  fetch_action: FetchAction | null;
}

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000;

const statusLabel = (status: string) => {
  if (status === 'started') return '実行中';
  if (status === 'succeeded') return '完了';
  if (status === 'failed') return '失敗';
  return status;
};

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const Total = observer(() => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [agodaTotals, setAgodaTotals] = useState<HotelOrderItemAgodaTotal[]>([]);
  const [arrangers, setArrangers] = useState<Arranger[]>([]);
  const [currentArranger, setCurrentArranger] = useState<Arranger | null>(null);
  const [suppliedItemId, setSuppliedItemId] = useState<number | undefined>(undefined);
  const [suppliedItemName, setSuppliedItemName] = useState<string>('');
  const [receiveDateFrom, setReceiveDateFrom] = useState<Moment | undefined>(
    utils.getParamMoment('receive_date_from') ?? moment()
  );
  const [receiveDateTo, setReceiveDateTo] = useState<Moment | undefined>(
    utils.getParamMoment('receive_date_to') ?? undefined
  );
  const [fetchAction, setFetchAction] = useState<FetchAction | null>(null);
  const [fetchFrom, setFetchFrom] = useState<Moment | undefined>(undefined);
  const [fetchTo, setFetchTo] = useState<Moment | undefined>(undefined);
  const [fetchError, setFetchError] = useState('');
  const [fetching, setFetching] = useState(false);
  const [pollMessage, setPollMessage] = useState('');
  const fetchRequestIdRef = useRef(0);
  const aliveRef = useRef(true);

  const fetchTodos = useCallback(() => {
    const requestId = fetchRequestIdRef.current + 1;
    fetchRequestIdRef.current = requestId;
    setIsLoading(true);
    const params = {
      receive_date_from: receiveDateFrom?.format('YYYY-MM-DD'),
      receive_date_to: receiveDateTo?.format('YYYY-MM-DD')
    };
    _.each(params, (value, key) => {
      if (value === undefined) {
        delete params[key as keyof typeof params];
      }
    });
    const queryParams = new URLSearchParams();
    queryParams.set('tab', 'AUTO_RESERVATION_INVENTORY');
    queryParams.set('sub_tab', 'AGODA_HOTEL');
    queryParams.set('agoda_hotel_type', 'TOTAL');
    if (receiveDateFrom) queryParams.set('receive_date_from', receiveDateFrom.format('YYYY-MM-DD'));
    if (receiveDateTo) queryParams.set('receive_date_to', receiveDateTo.format('YYYY-MM-DD'));
    history.push(`/arrangement/todo_list?${queryParams.toString()}`);

    Fetcher.get<AgodaTotalResponse>('/arrangement/auto_reservation_inventory/agoda_hotel/total.json', params)
      .then(res => {
        if (requestId !== fetchRequestIdRef.current || !aliveRef.current) return;

        setAgodaTotals(
          res.records.map(
            args => new HotelOrderItemAgodaTotal(convertHotelOrderItemAgodaTotalResponseToArgs(args))
          )
        );
        setArrangers(res.arrangers.map(args => new Arranger(args)));
        setSuppliedItemId(res.supplied_item_id || undefined);
        setSuppliedItemName(res.supplied_item_name || '');
        setCurrentArranger(new Arranger(res.current_arranger));
        setFetchAction(res.fetch_action);
      })
      .catch(() => {
        if (requestId !== fetchRequestIdRef.current || !aliveRef.current) return;

        Notification.error('データの取得に失敗しました');
      })
      .finally(() => {
        if (requestId === fetchRequestIdRef.current && aliveRef.current) setIsLoading(false);
      });
  }, [receiveDateFrom, receiveDateTo, history]);
  const fetchTodosRef = useRef(fetchTodos);
  fetchTodosRef.current = fetchTodos;

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  const pollFetchAction = useCallback(async (fetchActionId: number) => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (aliveRef.current && Date.now() < deadline) {
      await sleep(POLL_INTERVAL_MS);
      if (!aliveRef.current) return;

      try {
        const res = await Fetcher.get<{ fetch_action: FetchAction }>(
          `/arrangement/auto_reservation_inventory/agoda_hotel/fetch_actions/${fetchActionId}.json`
        );
        if (!aliveRef.current) return;

        setFetchAction(res.fetch_action);
        if (res.fetch_action.status === 'succeeded' || res.fetch_action.status === 'failed') {
          setPollMessage('');
          fetchTodosRef.current();
          return;
        }
      } catch {
        // 単発の通信エラーは次の確認まで待つ
      }
    }
    if (aliveRef.current) {
      setPollMessage('取得に時間がかかっています。しばらくして再読み込みしてください');
    }
  }, []);

  const validateFetchRange = () => {
    if (!fetchFrom || !fetchTo) return '開始日と終了日を入力してください';
    if (fetchTo.isBefore(fetchFrom, 'day')) return '終了日は開始日以降にしてください';
    if (fetchTo.diff(fetchFrom, 'days') + 1 > 62) return '取得期間は62日以内にしてください';
    return '';
  };

  const handleFetch = async () => {
    const error = validateFetchRange();
    setFetchError(error);
    if (error || !fetchFrom || !fetchTo) return;

    const fromDate = fetchFrom.format('YYYY-MM-DD');
    const toDate = fetchTo.format('YYYY-MM-DD');
    setFetching(true);
    setPollMessage('');
    try {
      const res = await Fetcher.post<{ fetch_action: FetchAction; error?: string }>(
        '/arrangement/auto_reservation_inventory/agoda_hotel/fetch_actions',
        { from_date: fromDate, to_date: toDate }
      );
      if (!aliveRef.current) return;
      if (res.error) {
        setFetchError(res.error);
        return;
      }
      setFetchAction(res.fetch_action);
      await pollFetchAction(res.fetch_action.id);
    } catch (e) {
      if (!aliveRef.current) return;

      const message =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Agoda予約の取得を開始できませんでした';
      setFetchError(message);
    } finally {
      if (aliveRef.current) setFetching(false);
    }
  };

  return (
    <>
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span>取得期間</span>
          <DateRangePicker from={fetchFrom} to={fetchTo} onFromChange={setFetchFrom} onToChange={setFetchTo} />
          <Button onClick={handleFetch} loading={fetching} disabled={fetching}>
            Agoda予約を取得
          </Button>
        </div>
        {fetchError && <div style={{ color: '#a72c09', marginTop: '8px' }}>{fetchError}</div>}
        {pollMessage && <div style={{ color: '#a72c09', marginTop: '8px' }}>{pollMessage}</div>}
        {fetchAction && (
          <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
            最新の取得: {statusLabel(fetchAction.status)} / 期間 {fetchAction.from_date} 〜 {fetchAction.to_date} /
            取り込み件数 {fetchAction.fetched_count}
            {fetchAction.finished_at ? ` / 終了 ${fetchAction.finished_at}` : ''}
            {fetchAction.status === 'failed' && fetchAction.error_message ? ` / ${fetchAction.error_message}` : ''}
          </div>
        )}
      </div>
      <TotalSearchArea
        receiveDateFrom={receiveDateFrom}
        receiveDateTo={receiveDateTo}
        suppliedItemName={suppliedItemName}
        setReceiveDateFrom={setReceiveDateFrom}
        setReceiveDateTo={setReceiveDateTo}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <TotalResultTable
          hotelOrderItemAgodaTotals={agodaTotals}
          arrangers={arrangers}
          currentArranger={currentArranger}
          suppliedItemId={suppliedItemId || null}
        />
      )}
    </>
  );
});

export default Total;
